'use client';

import { useEffect, useMemo, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient, type MeResponse, type UserBet } from '@/lib/api';
import { formatRelative, formatUsdt, pluralizeRu } from '@/lib/format';
import { getInitData } from '@/lib/tma';

export function PerformanceCard({
  user,
  refreshKey,
}: {
  user: MeResponse['user'];
  refreshKey: number;
}) {
  const [bets, setBets] = useState<UserBet[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const api = createApiClient(getInitData);
    api
      .myBets(100)
      .then((r) => setBets(r.bets))
      .catch((err: unknown) => {
        const e = err as ApiClientError;
        console.error('PerformanceCard: failed to load bets', e?.code);
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const stats = useMemo(() => {
    if (!bets) return null;
    const resolved = bets.filter(
      (b) => b.status === 'won' || b.status === 'lost' || b.status === 'refunded',
    );
    const active = bets.filter((b) => b.status === 'active');
    const won = resolved.filter((b) => b.status === 'won');
    const lost = resolved.filter((b) => b.status === 'lost');
    const winRate = resolved.length > 0 ? (won.length / resolved.length) * 100 : null;

    let bestWinMicro = 0;
    let bestWinBet: UserBet | null = null;
    for (const b of won) {
      const profit = (b.payout_micro ?? 0) - b.amount_micro;
      if (profit > bestWinMicro) {
        bestWinMicro = profit;
        bestWinBet = b;
      }
    }

    let worstLossMicro = 0;
    let worstLossBet: UserBet | null = null;
    for (const b of lost) {
      if (b.amount_micro > worstLossMicro) {
        worstLossMicro = b.amount_micro;
        worstLossBet = b;
      }
    }

    const totalStakeMicro = bets.reduce((s, b) => s + b.amount_micro, 0);
    const avgStakeMicro = bets.length > 0 ? Math.round(totalStakeMicro / bets.length) : 0;
    const maxStakeMicro = bets.reduce((m, b) => Math.max(m, b.amount_micro), 0);

    const orderedResolved = [...resolved].sort(
      (a, b) => (b.paid_at ?? 0) - (a.paid_at ?? 0),
    );
    let currentStreak = 0;
    let streakSide: 'wins' | 'losses' | null = null;
    for (const b of orderedResolved) {
      if (currentStreak === 0) {
        if (b.status === 'won') {
          streakSide = 'wins';
          currentStreak = 1;
        } else if (b.status === 'lost') {
          streakSide = 'losses';
          currentStreak = 1;
        } else {
          break;
        }
      } else {
        if (
          (streakSide === 'wins' && b.status === 'won') ||
          (streakSide === 'losses' && b.status === 'lost')
        ) {
          currentStreak += 1;
        } else {
          break;
        }
      }
    }

    const lastBetAt = bets.length > 0
      ? bets.reduce((m, b) => Math.max(m, b.created_at), 0)
      : null;

    return {
      betsCount: bets.length,
      activeCount: active.length,
      resolvedCount: resolved.length,
      wonCount: won.length,
      lostCount: lost.length,
      winRate,
      bestWinMicro,
      bestWinBet,
      worstLossMicro,
      worstLossBet,
      avgStakeMicro,
      maxStakeMicro,
      currentStreak,
      streakSide,
      lastBetAt,
    };
  }, [bets]);

  if (loading) {
    return (
      <div className="bg-bg-elev p-5 mb-4 flex justify-center">
        <Spinner size={20} />
      </div>
    );
  }
  if (!stats) return null;

  const profit = user.stats.total_profit_usd_cents;
  const volume = user.stats.total_volume_usd_cents;
  const hasResolutions = stats.resolvedCount > 0;
  const profitPctOfVolume = hasResolutions && volume > 0 ? (profit / volume) * 100 : null;
  const profitColor = profit > 0 ? 'text-yes' : profit < 0 ? 'text-no' : 'text-text-tertiary';

  return (
    <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-accent">
      <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em] mb-3">
        ● СТАТИСТИКА
      </div>

      {/* Top row: Ставок, Объём */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Stat label="СТАВОК" value={String(stats.betsCount)}>
          {stats.activeCount > 0 && (
            <>
              {stats.activeCount}{' '}
              {pluralizeRu(stats.activeCount, 'активна', 'активны', 'активны')}
            </>
          )}
          {stats.activeCount > 0 && stats.resolvedCount > 0 && ' · '}
          {stats.resolvedCount > 0 && (
            <>
              {stats.resolvedCount}{' '}
              {pluralizeRu(stats.resolvedCount, 'закрыта', 'закрыты', 'закрыто')}
            </>
          )}
          {stats.betsCount === 0 && '—'}
        </Stat>
        <Stat label="ОБЪЁМ" value={`$${(volume / 100).toFixed(volume % 100 === 0 ? 0 : 2)}`}>
          {stats.betsCount > 0 ? (
            <>ср. {formatUsdt(stats.avgStakeMicro)} · max {formatUsdt(stats.maxStakeMicro)}</>
          ) : (
            'нет ставок'
          )}
        </Stat>
      </div>

      {/* Bottom row: Win rate, Прибыль */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Stat
          label="WIN RATE"
          value={stats.winRate === null ? '—' : `${stats.winRate.toFixed(0)}%`}
          dim={stats.winRate === null}
        >
          {stats.winRate === null
            ? 'после первой резолюции'
            : `${stats.wonCount}/${stats.resolvedCount} wins`}
        </Stat>
        <Stat
          label="ПРИБЫЛЬ"
          value={
            !hasResolutions
              ? '—'
              : `${profit >= 0 ? '+' : '−'}$${(Math.abs(profit) / 100).toFixed(2)}`
          }
          dim={!hasResolutions}
          valueClass={hasResolutions ? profitColor : ''}
        >
          {!hasResolutions
            ? 'появится после резолюций'
            : profitPctOfVolume !== null
              ? `${profitPctOfVolume >= 0 ? '+' : ''}${profitPctOfVolume.toFixed(1)}% от объёма`
              : ''}
        </Stat>
      </div>

      {/* Inline row: Стрик · Последняя активность */}
      <div className="flex items-center justify-between font-mono text-[10px] pt-3 border-t border-line tracking-[0.05em]">
        <span className="text-text-tertiary">
          СТРИК:{' '}
          {stats.currentStreak === 0 ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            <span
              className={
                stats.streakSide === 'wins'
                  ? 'text-yes font-bold'
                  : 'text-no font-bold'
              }
            >
              {stats.streakSide === 'wins' ? '🔥' : '❄️'} {stats.currentStreak}
            </span>
          )}
        </span>
        <span className="text-text-tertiary">
          {stats.lastBetAt
            ? `ПОСЛ. ${formatRelative(stats.lastBetAt).toUpperCase()}`
            : 'НЕТ СТАВОК'}
        </span>
      </div>

      {/* Best/Worst */}
      {(stats.bestWinBet || stats.worstLossBet) && (
        <div className="space-y-2 pt-3 mt-3 border-t border-line">
          {stats.bestWinBet && (
            <BestWorstRow
              icon="🏆"
              label="ЛУЧШИЙ WIN"
              question={stats.bestWinBet.market_question}
              amount={`+${formatUsdt(stats.bestWinMicro)}`}
              colorClass="text-yes"
            />
          )}
          {stats.worstLossBet && (
            <BestWorstRow
              icon="💀"
              label="ХУДШИЙ LOSS"
              question={stats.worstLossBet.market_question}
              amount={`−${formatUsdt(stats.worstLossMicro)}`}
              colorClass="text-no"
            />
          )}
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  dim,
  valueClass = '',
  children,
}: {
  label: string;
  value: string;
  dim?: boolean;
  valueClass?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-1">
        {label}
      </div>
      <div
        className={`display text-[22px] tabular-nums leading-none ${
          dim ? 'text-text-tertiary' : valueClass || 'text-white'
        }`}
      >
        {value}
      </div>
      <div className="font-mono text-[10px] text-text-tertiary mt-1">{children}</div>
    </div>
  );
}

function BestWorstRow({
  icon,
  label,
  question,
  amount,
  colorClass,
}: {
  icon: string;
  label: string;
  question: string;
  amount: string;
  colorClass: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <span className="text-base leading-none">{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em]">
            {label}
          </div>
          <p className="text-[12px] leading-snug truncate">{question}</p>
        </div>
      </div>
      <span className={`${colorClass} font-bold tabular-nums whitespace-nowrap text-[13px]`}>
        {amount}
      </span>
    </div>
  );
}
